'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Cloud } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import { DisconnectButton, IntegrationRow, ROW_BUTTON } from './IntegrationList';
import { useOAuthReturn } from './useOAuthReturn';

// Server-side OneDrive: connects the user's OneDrive to Factiii's servers so the
// File Vault can read + embed documents. Distinct from the runner "Private
// connections" OneDrive (which stays on the runner machine). Available to any
// logged-in user — all vault operations are scoped to their own OneDrive and
// documents. Connection lives here; sync/search live on /onedrive.
const OneDriveServerCard = () => {
  const $ = _c(21);
  const queryClient = useQueryClient();
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [connecting, setConnecting] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["onedriveServerStatus"],
      queryFn: _temp,
      retry: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: status,
    isLoading
  } = useQuery(t0);
  let t1;
  if ($[1] !== queryClient) {
    t1 = async (code, oauthState) => {
      const [err] = await client.onedriveEmbed.connect.mutate({
        code,
        state: oauthState
      });
      if (err) {
        toast(err.message || "Could not connect OneDrive", "", "error");
        return;
      }
      toast("OneDrive connected.");
      await queryClient.invalidateQueries({
        queryKey: ["onedriveServerStatus"]
      });
    };
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useOAuthReturn("onedrive", "OneDrive", t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async () => {
      setConnecting(true);
      const [err_0, res] = await client.onedriveEmbed.authUrl.mutate();
      if (err_0) {
        setConnecting(false);
        toast(err_0.message || "Could not start OneDrive connect", "", "error");
        return;
      }
      window.location.href = res.url;
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const connect = t2;
  let t3;
  if ($[4] !== queryClient) {
    t3 = async () => {
      const [err_1] = await client.onedriveEmbed.disconnect.mutate();
      if (!err_1) {
        toast("OneDrive disconnected.");
        await queryClient.invalidateQueries({
          queryKey: ["onedriveServerStatus"]
        });
      }
      setShowDisconnect(false);
    };
    $[4] = queryClient;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleDisconnect = t3;
  const connected = !!status?.connected;
  const configured = status?.configured ?? true;
  const statusText = isLoading ? "Checking\u2026" : !configured ? "Not set up on this server yet \u2014 an admin adds the client id and secret first" : connected ? `Connected${status?.accountName ? ` as ${status.accountName}` : ""}` : "Grants Factiii\u2019s servers access to the files you import";
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Cloud size={18} />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== configured || $[8] !== connected || $[9] !== connecting || $[10] !== isLoading) {
    t5 = isLoading ? undefined : connected ? <><Link href="/onedrive" className="rounded-full border border-ds-border-standard bg-background-light px-4 py-1.5 text-sm font-emphasis text-textPrimary no-underline transition-all duration-300 hover:bg-ds-border-hover">Open vault</Link><DisconnectButton label="OneDrive" onClick={() => setShowDisconnect(true)} /></> : configured && <Button text="Connect" variant="SECONDARY" classNames={ROW_BUTTON} loading={connecting} onClick={connect} />;
    $[7] = configured;
    $[8] = connected;
    $[9] = connecting;
    $[10] = isLoading;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== statusText || $[13] !== t5) {
    t6 = <IntegrationRow icon={t4} name="OneDrive" status={statusText} actions={t5} />;
    $[12] = statusText;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== handleDisconnect || $[16] !== showDisconnect) {
    t7 = showDisconnect && <Confirmation title="Disconnect OneDrive" message="Factiii will lose access to your OneDrive. Already-imported documents stay searchable, but you won't be able to import or download originals until you reconnect." cancelHandler={() => setShowDisconnect(false)} confirmHandler={handleDisconnect} actionButtonText="Disconnect" variant="DANGER" />;
    $[15] = handleDisconnect;
    $[16] = showDisconnect;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
export default OneDriveServerCard;
function _temp() {
  return unwrapFromResult(client.onedriveEmbed.status.query());
}